'use client';

/**
 * LE MÉMO D'ACCUEIL — « عسلامة {prénom} », le poste du jour et sa check-list.
 *
 * Idée de la RQ (26/08/2026) : en arrivant le matin, la question est « qu'est-ce
 * que je tiens aujourd'hui, et qu'est-ce que je dois vérifier ». Elle se
 * répondait en ouvrant le planning, puis un fichier Word.
 *
 * ⚠️ LE SALUT A DÉMÉNAGÉ (27/08/2026). « عسلامة {prénom} » et l'horaire du jour
 * ne sont plus ici : ils s'affichent à la CONNEXION, fondus dans l'écran qui
 * annonçait déjà le rôle reconnu (`shared/LoginPanel.tsx`, pastille
 * « Aujourd'hui »). Les deux disaient la même chose à trois secondes
 * d'intervalle. Ce qui reste ici est ce qu'on revient consulter dans la
 * journée — la check-list — et non ce qu'on lit une fois en arrivant.
 *
 * ⚠️ CE N'EST PAS UN POP-UP, ET C'EST DÉLIBÉRÉ. L'application en pose déjà un au
 * démarrage (les annonces programmées). En empiler un second, c'est le début du
 * bruit : deux fenêtres à fermer d'affilée s'apprennent à fermer sans lire. Le
 * mémo vit donc en haut du tableau de bord, là où le regard tombe déjà, il reste
 * toute la journée et ne demande rien.
 *
 * ⚠️ LES MAINTENANCES DU POSTE S'AFFICHENT À CÔTÉ (RQ, 05/10/2026) : « si je
 * suis sur un poste et que j'ai des maintenances à faire, je veux qu'elles
 * s'affichent à côté de ma check-list ». Deux colonnes, et c'est voulu : la
 * check-list est ce qu'on COCHE pour soi (rien ne s'enregistre, cf. ci-dessous),
 * la maintenance est ce qu'on DÉCLARE au registre (un acte signé, daté, opposable
 * en audit). Les mêler dans une seule liste de cases ferait croire que cocher
 * suffit — ou, dans l'autre sens, que la check-list laisse une trace.
 *
 * ⚠️ LES CASES NE SONT PAS ENREGISTRÉES (décision de la RQ). Elles vivent dans
 * le navigateur et se remettent à zéro chaque jour. Une liste cochable ressemble
 * à s'y méprendre à une preuve : quelqu'un qui coche vingt-deux cases croit
 * avoir tracé sa garde, alors que la grille papier reste la seule preuve
 * opposable. La réserve est donc ÉCRITE à l'écran, pas seulement dans ce
 * commentaire.
 */

import React from 'react';
import { useGed } from '@/lib/store';
import { styleColonnes, STYLE_TACHE_COLONNE } from '@/lib/colonnes-taches';
import { I, card, useBusy } from '@/components/ui';
import { fmtDate } from '@/lib/meta';
import type { MaintenanceOccurrence } from '@/lib/types';
/* ⚠️ LA FENÊTRE « DÉCLARER FAITE » ET LA PASTILLE D'ÉTAT SONT CELLES DU PLAN,
   réemployées telles quelles. En recopier une version « légère » ici aurait
   produit deux formulaires de preuve pour un même acte — et c'est celui qu'on
   ne regarde pas qui finirait par oublier les opérations ou le signalement
   d'anomalie. La fenêtre s'ouvre par le magasin (`ouvrirDeclaration`, spec
   002, R14), comme depuis toutes les autres portes. */
import { EtatPill } from '@/components/screens/ressources/Maintenances';
import { faiteDirecte, rangMemo, refEcheance } from '@/lib/declaration-maintenance';
import { cheminMaintenanceAppareil } from '@/lib/maintenance-appareil';
import { ouvrirMaintenanceAppareil } from '@/lib/versMaintenanceAppareil';
import { clicDuNavigateur } from '@/shared/Tableau';

import { Bouton } from '@/shared/Bouton';
interface Rubrique { titre: string; taches: string[] }
interface Checklist {
  code: string; libelle: string; source: string; nbTaches: number;
  rubriques: Rubrique[];
  appelle?: { code: string; libelle: string; nbTaches: number; rubriques: Rubrique[] }[];
}
interface PosteDuJour {
  code: string; libelle: string; heureDebut: string; heureFin: string;
  nature: string; checklist: Checklist | null;
}
/** Une échéance de maintenance servie par le mémo : l'occurrence du registre,
 *  plus ce que l'écran ne peut pas deviner — les opérations de la ligne de plan,
 *  le poste de l'appelant auquel elle se rattache, et s'il s'agit d'un retard. */
interface MaintenanceMemo extends MaintenanceOccurrence {
  operations: string[];
  /** Le poste À MOI d'où elle vient — vide quand elle n'arrive que par mon nom. */
  posteCode: string;
  retard: boolean;
}
interface MonJourRep {
  ok: boolean; date: string; salut: string; prenom: string; phrase: string;
  enConge: boolean; postes: PosteDuJour[]; reserve: string;
  maintenances?: MaintenanceMemo[];
  /** ⚠️ « Aucune maintenance » et « on n'a pas regardé » ne se disent pas pareil :
   *  sans ce drapeau, une liste vide ferait affirmer à l'écran qu'il n'y a rien
   *  à entretenir alors que la route n'a rien projeté (congé, aucun poste). */
  maintenancesLues?: boolean;
}

/** La clé du jour : les cases d'hier ne doivent pas ressusciter ce matin. */
const cleDuJour = (date: string, codePoste: string) => `bio24.memo.${date}.${codePoste}`;

export function MonJour() {
  const session = useGed((s) => s.session);
  const [rep, setRep] = React.useState<MonJourRep | null>(null);
  const [ouvert, setOuvert] = React.useState<string | null>(null);
  /* Une déclaration faite AILLEURS (le calendrier juste en dessous) relit aussi le mémo. */
  const declarationsFaites = useGed((s) => s.declarationsFaites);
  const ouvrirDeclaration = useGed((s) => s.ouvrirDeclaration);
  /** L'échéance ouverte en détail — la fenêtre unique « Déclarer faite », pas une copie. */
  const ouvrirDetail = React.useCallback((m: MaintenanceMemo) => ouvrirDeclaration(refEcheance(m), {
    occ: m, operationsConnues: m.operations,
  }), [ouvrirDeclaration]);

  const charger = React.useCallback(async () => {
    const d = await fetch('/api/ged/ged/rh/mon-jour', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      /* Un refus ne montre RIEN plutôt qu'un message d'erreur : le mémo est un
         agrément, pas une fonction. Le faire crier ferait passer une politesse
         pour une panne. */
      .catch(() => null);
    if (d?.ok) setRep(d as MonJourRep);
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- lecture réseau à l'ouverture
  React.useEffect(() => { void charger(); }, [charger, session.key, declarationsFaites]);

  /* ⚠️ RIEN À DIRE, RIEN À MONTRER. Depuis que le salut est parti à la
     connexion, une journée sans poste affiché ne laisserait qu'une carte vide
     en haut du tableau de bord — un cadre qui se lit comme une panne.

     L'absence validée se retire AUSSI, et pour la raison que dit déjà la
     route : rappeler sa check-list à quelqu'un en congé est absurde. Il le lit
     à la connexion, où la pastille du jour porte la phrase du serveur. */
  if (!rep || rep.postes.length === 0 || rep.enConge) return null;

  /* ⚠️ LA COLONNE DE DROITE NE S'AFFICHE QUE SI LA ROUTE A RÉPONDU. Un cadre
     « aucune maintenance » dressé sans projection serait une affirmation que
     personne n'a vérifiée — et c'est celle qu'on croit le matin. */
  const mnt = rep.maintenances || [];
  const avecMaintenances = rep.maintenancesLues === true;

  return (
    <div style={{ ...card, padding: '14px 18px' }}>
      <div className="ged-2col" style={{
        display: 'grid',
        gridTemplateColumns: avecMaintenances ? 'minmax(0, 1fr) minmax(0, 340px)' : 'minmax(0, 1fr)',
        gap: 18, alignItems: 'start',
      }}>

        <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Ma check-list du jour
          </div>

          {rep.postes.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {rep.postes.map((p) => (
                <span key={p.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {p.checklist ? (
                    <Bouton variante={ouvert === p.code ? 'primaire' : 'contour'} taille="sm" type="button" aria-expanded={ouvert === p.code} onClick={() => setOuvert(ouvert === p.code ? null : p.code)}>
                      <I n="check" s={13} /> {p.checklist.libelle} · {p.checklist.nbTaches} points
                    </Bouton>
                  ) : (
                    /* Pas de check-list pour ce poste : on le DIT. Un bouton absent
                       se lit comme un oubli de l'application ; une phrase se lit
                       comme un état du référentiel. */
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {p.libelle} — aucune check-list n’est encore rattachée à ce poste
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {rep.postes.filter((p) => p.code === ouvert && p.checklist).map((p) => (
            <Memo key={p.code} poste={p} date={rep.date} reserve={rep.reserve} />
          ))}
        </div>

        {avecMaintenances && (
          <MaintenancesDuPoste liste={mnt} jour={rep.date} moi={session.key}
            plusieursPostes={rep.postes.length > 1}
            onDetail={ouvrirDetail} onFait={charger} />
        )}
      </div>

    </div>
  );
}

/* ═════════════════════════ les maintenances du poste tenu ═══════════════════*/

/**
 * Ce que l'appareil du poste attend aujourd'hui — et ce qu'il attendait déjà.
 *
 * ⚠️ DEUX GESTES, ET PAS UN SEUL (demande de la RQ) : « un bouton direct permet
 * de l'acquitter ou bien en cliquant dessus je peux lire les détails ». La ligne
 * entière ouvre le détail ; le bouton déclare faite sur place. N'avoir que le
 * second ferait signer sans lire ; n'avoir que le premier ferait traverser une
 * fenêtre pour la maintenance journalière de sept heures du matin.
 *
 * ⚠️ LE BOUTON DIRECT DÉCLARE LES OPÉRATIONS FAITES, et il le DIT (le nombre est
 * sur la ligne, la conséquence dans l'infobulle). Les envoyer vides aurait
 * enregistré « 8 opérations NON FAITES » sur un acte que la personne vient de
 * déclarer fait — un acquittement qui dit le contraire de ce qui s'est passé.
 * Pour n'en déclarer qu'une partie, le détail est à un clic.
 *
 * ⚠️ ON NE MASQUE PAS CE QUI EST ATTRIBUÉ À QUELQU'UN D'AUTRE. Une paillasse
 * tenue à deux ne désigne qu'UNE personne au calcul ; la maintenance reste celle
 * du poste. Le nom du destinataire est donc écrit quand ce n'est pas le mien, et
 * l'acquittement se fait en MON nom — c'est moi qui l'ai faite.
 */
function MaintenancesDuPoste({ liste, jour, moi, plusieursPostes, onDetail, onFait }: {
  liste: MaintenanceMemo[];
  jour: string;
  moi: string;
  plusieursPostes: boolean;
  onDetail: (m: MaintenanceMemo) => void;
  onFait: () => void;
}) {
  const { smq, loadMaintenances, session } = useGed();
  const { busy, run } = useBusy();
  /* ⚠️ LE BOUTON GRISÉ SANS PHRASE EST UN PIÈGE CONNU. `POST .../acquitter` est
     rattaché à l'acte `voir` de la section « maintenances » précisément pour que
     la personne à qui l'agenda pousse la maintenance du matin puisse la déclarer
     faite — mais le paramétrage peut fermer la section à un profil. On MONTRE
     alors quand même ce qu'il y a à faire (c'est le travail du poste, pas une
     information de registre) et l'on dit pourquoi la déclaration n'est pas là,
     au lieu d'offrir un bouton qui finirait en refus.
     `sections` absent = moteur antérieur au lot N6 : on retombe sur l'avant, et
     personne ne perd son bouton. */
  const peutDeclarer = session.sections ? session.sections.maintenances?.voir !== false : true;

  const declarerFaite = (m: MaintenanceMemo) => run(`${m.planId}|${m.date}`, async () => {
    const ok = await smq(`ged/maintenances/${m.planId}/acquitter`, {
      datePrevue: m.date, dateFaite: jour, operations: m.operations,
    }, 'Maintenance déclarée faite.');
    if (!ok) return;
    /* Le mémo ET le plan : le calendrier du tableau de bord est juste en
       dessous, et il garderait sinon une échéance qu'on vient de déclarer faite. */
    onFait();
    await loadMaintenances();
  });

  /* ⚠️ « FAITE » NE DÉCLARE D'UN CLIC QUE LE JOUR MÊME, ET LES ROUTINES
     (`faiteDirecte` ; Inès, 28/10/2026, puis spec 002, EF-012). Le jour même,
     la date est aujourd'hui sans rien supposer. Une échéance PASSÉE ouvre la
     fenêtre, qui demande le jour du geste : le bouton direct la datait
     d'aujourd'hui (incohérence 16). Au-delà de la journalière et de
     l'hebdomadaire — la trimestrielle du D10 et ses quatorze étapes —, chaque
     opération se déclare dans la fenêtre, faite ou non faite, avec son motif. */
  const direct = (m: MaintenanceMemo) => faiteDirecte(m, jour);

  /* TROIS RANGS (incohérence 17) : le travail du jour, l'échu encore « dans le
     délai » (le plan le dit ainsi), et ce qui est à rattraper. */
  const duJour = liste.filter((m) => rangMemo(m, jour) === 'jour');
  const dansLeDelai = liste.filter((m) => rangMemo(m, jour) === 'delai');
  const retards = liste.filter((m) => rangMemo(m, jour) === 'rattraper');

  const ligne = (m: MaintenanceMemo) => {
    const cle = `${m.planId}|${m.date}`;
    const pourAutrui = !!m.responsableKey && m.responsableKey !== moi;
    return (
      <li key={cle} style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'start',
        padding: '7px 0', borderTop: '1px solid var(--border-subtle)',
      }}>
        {/* La LIGNE ouvre le détail. Un vrai bouton, et non un `div` cliquable :
            au clavier comme au lecteur d'écran, une ligne qui agit doit s'annoncer.
            L'APPAREIL, lui, est un lien à part vers son onglet Maintenance
            (spec 002, EF-033) : un bouton ne s'emboîte pas dans un bouton. */}
        <div style={{ minWidth: 0 }}>
        <button type="button" onClick={() => onDetail(m)}
          title="Ouvrir le détail de cette maintenance (opérations, commentaire, anomalie)"
          style={{
            background: 'none', border: 0, padding: 0, margin: 0, textAlign: 'start',
            cursor: 'pointer', font: 'inherit', color: 'inherit', minWidth: 0,
          }}>
          <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.libelle}</span>
            <EtatPill o={m} />
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {/* L'échéance DATÉE dès qu'elle n'est pas celle du jour : « en retard »
                sans sa date ne dit pas de combien, donc ne hiérarchise rien. */}
            {[
              m.date !== jour ? `échéance du ${fmtDate(m.date)}` : '',
              m.heure || '',
              plusieursPostes && m.posteCode ? m.posteCode : '',
              m.operations.length > 0 ? `${m.operations.length} opération${m.operations.length > 1 ? 's' : ''}` : '',
            ].filter(Boolean).join(' · ')}
          </span>
          {pourAutrui && (
            <span style={{ display: 'block', fontSize: 12, color: 'var(--amber-600, #b45309)', marginTop: 2 }}>
              attribuée à {m.responsableLibelle || m.responsableKey} — la déclarer ici la signe de VOTRE nom
            </span>
          )}
          {/* Un report se CONSTATE au moment de déclarer, pas trois mois plus tard. */}
          {m.deplacee && (
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{m.motifReport}</span>
          )}
        </button>
        <a href={cheminMaintenanceAppareil(m.equipementCode)} title="Ouvrir l’onglet Maintenance de l’appareil"
          onClick={(ev) => { if (!clicDuNavigateur(ev) && ouvrirMaintenanceAppareil(m.equipementCode, {}, 'Mon jour')) ev.preventDefault(); }}
          style={{ display: 'inline-block', fontSize: 12, color: 'var(--color-primary-active)', textDecoration: 'none', marginTop: 2 }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{m.equipementCode}</span>{' '}{m.equipementNom}
        </a>
        </div>
        {peutDeclarer && !direct(m) ? (
          <Bouton taille="sm" type="button" onClick={() => onDetail(m)}
            title={m.date < jour
              ? `Échéance du ${fmtDate(m.date)} : la fenêtre demande le jour où elle a été faite.`
              : `Ouvrir la liste de contrôle : les ${m.operations.length} opérations se déclarent une par une, faites ou non faites.`}>
            <I n="check" s={13} /> Faite
          </Bouton>
        ) : peutDeclarer ? (
          <Bouton taille="sm" type="button" chargement={busy === cle}
            libelleChargement="…" onClick={() => declarerFaite(m)}
            title={m.operations.length
              ? `Déclarer faite aujourd’hui — les ${m.operations.length} opérations seront enregistrées comme FAITES. Pour n’en déclarer qu’une partie, ouvrez le détail.`
              : 'Déclarer cette maintenance faite aujourd’hui.'}>
            <I n="check" s={13} /> Faite
          </Bouton>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 120, textAlign: 'end' }}>
            déclaration fermée à votre profil — à signaler au RQ
          </span>
        )}
      </li>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        <I n="wrench" s={12} /> Maintenances de mon poste
      </div>

      {liste.length === 0 ? (
        /* L'absence est une AFFIRMATION, et celle-ci est tenable : la route a
           projeté le plan sur les postes tenus ce jour-là. */
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Rien à entretenir aujourd’hui sur les postes que vous tenez.
        </p>
      ) : (
        <>
          {duJour.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{duJour.map(ligne)}</ul>
          )}
          {dansLeDelai.length > 0 && (
            <>
              {/* ÉCHU, MAIS DANS LE DÉLAI : le plan ne le dit pas en retard, le
                  mémo non plus (incohérence 17). */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber-600)', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4 }}>
                Échues, dans le délai
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{dansLeDelai.map(ligne)}</ul>
            </>
          )}
          {retards.length > 0 && (
            <>
              {/* LE RETARD À PART, et nommé. Mêlé au travail du jour, il se lirait
                  comme une tâche d'aujourd'hui qu'on peut remettre à demain. */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-600)', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4 }}>
                Non faites, à rattraper
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{retards.map(ligne)}</ul>
            </>
          )}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            {/* ⚠️ LA FENÊTRE EST DITE. Sans cette phrase, « voilà mes maintenances »
                se lirait comme « voilà TOUTES mes maintenances », alors que le
                retard n'est remonté que depuis le mois précédent — la même fenêtre
                que le registre, qui porte le reste. */}
            Le retard remonté commence au mois précédent, comme au registre des maintenances.
            « Faite » déclare d’un clic la routine du jour, à votre nom ; une échéance passée ouvre la fenêtre, qui demande le jour du geste.
          </p>
        </>
      )}
    </div>
  );
}

function Memo({ poste, date, reserve }: { poste: PosteDuJour; date: string; reserve: string }) {
  const cl = poste.checklist as Checklist;
  /* Toutes les listes du poste, la sienne PUIS celles qu'elle appelle : la
     check-list de garde renvoie explicitement à celles de la réception et du
     prélèvement — c'est écrit dans le document, ce n'est pas une déduction. */
  const listes = [cl, ...(cl.appelle || [])];
  const cle = cleDuJour(date, poste.code);

  const [coches, setCoches] = React.useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const brut = window.localStorage.getItem(cle);
      return new Set(brut ? (JSON.parse(brut) as string[]) : []);
    } catch { return new Set(); }
  });

  const basculer = (id: string) => {
    setCoches((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      try { window.localStorage.setItem(cle, JSON.stringify(Array.from(n))); } catch { /* mode privé : le mémo marche quand même, il ne survit pas au rechargement */ }
      return n;
    });
  };

  const total = listes.reduce((n, l) => n + l.nbTaches, 0);

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <I n="info" s={12} />
        <span><strong>{coches.size} / {total}</strong> — {reserve}</span>
      </p>

      {listes.map((l) => (
        <div key={l.code}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, letterSpacing: '-.005em' }}>
            {l.libelle}
            {l.code !== cl.code && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12.5 }}> — appelée par la check-list du poste</span>
            )}
          </h4>
          {l.rubriques.map((r) => (
            <div key={r.titre} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>
                {r.titre}
              </div>
              {/* ── LES TÂCHES EN COLONNES (RQ, 05/10/2026) ──────────────────
                  « Si les tâches pouvaient être affichées en colonnes ce serait
                  beaucoup mieux. » Le mémo de garde aligne 126 tâches : il
                  fallait plusieurs écrans pour voir ce qu'on doit faire.
                  ⚠️ `display: grid` A ÉTÉ RETIRÉ : une grille et un conteneur
                  multi-colonnes ne se composent pas — la grille l'emporte et
                  les colonnes n'ont aucun effet. L'espacement passe donc par
                  le `padding` de chaque ligne.
                  ⚠️ `STYLE_TACHE_COLONNE` sur chaque `<li>` n'est pas
                  décoratif : sans lui, une coupure de colonne sépare la case à
                  cocher de son libellé, et l'on coche la mauvaise tâche sans
                  que rien ne le dise. */}
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, ...styleColonnes(r.taches.length) }}>
                {r.taches.map((t, i) => {
                  const id = `${l.code}|${r.titre}|${i}`;
                  const fait = coches.has(id);
                  return (
                    <li key={id} style={STYLE_TACHE_COLONNE}>
                      <label style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, cursor: 'pointer',
                        lineHeight: 1.45, padding: '2px 0',
                        color: fait ? 'var(--text-muted)' : 'var(--text-body)',
                        textDecoration: fait ? 'line-through' : 'none',
                      }}>
                        <input type="checkbox" checked={fait} onChange={() => basculer(id)} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span>{t}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
